import { useId, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  EditableToken,
  RichTextContent,
  useEditMode,
} from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';

/**
 * FeeTransparency — Harborstone fee disclosure accordion.
 * Required / Optional / Situational panels with Fee Name · Cost · Frequency · Fee Type.
 * Per-fee cost notes surface as a dotted-underline tooltip on the cost value.
 */

const NA = '—';
function isNA(value: string | null | undefined): boolean {
  if (value == null) return true;
  const v = value.trim();
  return v === '' || v === NA;
}

/** True when the cost should show a leading $ (numeric amounts without one). */
function shouldPrefixDollar(value: string | null | undefined): boolean {
  if (isNA(value)) return false;
  const v = value!.trim();
  if (v.startsWith('$')) return false;
  return /^[\d.,]/.test(v);
}

function costClass(
  value: string | null | undefined,
  /** Dotted underline only when a tooltip disclaimer is attached. */
  hasTooltip = false,
): string {
  return cn(
    'ft-cost',
    shouldPrefixDollar(value) && 'ft-cost--dollar',
    hasTooltip && 'ft-cost--tooltip',
  );
}

type FeeType = 'Required' | 'Optional' | 'Situational';
type BadgeVariant = 'required' | 'optional' | 'situational';

type FeeRow = {
  name: string;
  value: string | null | undefined;
  frequency: string;
  feeType: FeeType;
  badge: BadgeVariant;
  node: ReactNode;
};

function CostWithDisclaimer({
  value,
  disclaimer,
  children,
}: {
  value: string | null | undefined;
  disclaimer?: string;
  children: ReactNode;
}) {
  const tipId = useId();
  const hasTip = Boolean(disclaimer && disclaimer.trim());

  return (
    <span className={cn('ft-cost-wrap', hasTip && 'ft-cost-wrap--tip')}>
      <span
        className={costClass(value, hasTip)}
        tabIndex={hasTip ? 0 : undefined}
        aria-describedby={hasTip ? tipId : undefined}
      >
        {children}
      </span>
      {hasTip ? (
        <span role="tooltip" id={tipId} className="ft-cost-tip">
          {disclaimer}
        </span>
      ) : null}
    </span>
  );
}

function FeeBadge({ variant, children }: { variant: BadgeVariant; children: ReactNode }) {
  return (
    <span className={cn('ft-badge', `ft-badge--${variant}`)}>{children}</span>
  );
}

function FeeTable({
  rows,
  showAll,
  footer,
}: {
  rows: FeeRow[];
  showAll: boolean;
  footer?: ReactNode;
}) {
  const visible = showAll ? rows : rows.filter((r) => !isNA(r.value));

  if (visible.length === 0 && !footer) {
    return (
      <p className="ft-empty">No fees in this category apply at this community.</p>
    );
  }

  return (
    <div className="ft-table-wrap">
      {visible.length > 0 ? (
        <table className="ft-table">
          <thead>
            <tr>
              <th scope="col">Fee Name</th>
              <th scope="col">Cost</th>
              <th scope="col">Frequency</th>
              <th scope="col">Fee Type</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.name}>
                <td data-label="Fee Name">{row.name}</td>
                <td data-label="Cost">{row.node}</td>
                <td data-label="Frequency">{row.frequency}</td>
                <td data-label="Fee Type">
                  <FeeBadge variant={row.badge}>{row.feeType}</FeeBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="ft-empty">No fees in this category apply at this community.</p>
      )}
      {footer}
    </div>
  );
}

function FeePanel({
  title,
  badgeLabel,
  badgeVariant,
  description,
  rows,
  open,
  onToggle,
  showAll,
  footer,
}: {
  title: string;
  badgeLabel: string;
  badgeVariant: BadgeVariant;
  description: ReactNode;
  rows: FeeRow[];
  open: boolean;
  onToggle: () => void;
  showAll: boolean;
  footer?: ReactNode;
}) {
  return (
    <div className={cn('ft-panel', open && 'ft-panel--open')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="ft-panel-trigger"
      >
        <span className="ft-panel-title">
          {title}
          <FeeBadge variant={badgeVariant}>{badgeLabel}</FeeBadge>
        </span>
        <span className="ft-panel-icon" aria-hidden="true" />
      </button>

      <div className="ft-panel-body" data-open={open} aria-hidden={!open}>
        <div className="ft-panel-body-inner">
          {description}
          <FeeTable rows={rows} showAll={showAll} footer={footer} />
        </div>
      </div>
    </div>
  );
}

interface FeeTransparencyProps {
  /** When false, hides the section heading and intro (Floor Plans). Default true. */
  showHeader?: boolean;
  /** When true, stretches the content to the full page width (Floor Plans). */
  wide?: boolean;
}

export function FeeTransparency({ showHeader = true, wide = false }: FeeTransparencyProps) {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const heading = useContentToken('feetransparency.heading');

  const applicationFee = useContentToken('feetransparency.application_fee');
  const securityDeposit = useContentToken('feetransparency.security_deposit');
  const reservationFee = useContentToken('feetransparency.reservation_fee');
  const utilityNewAccountFee = useContentToken('feetransparency.utility_new_account_fee');
  const utilityWaterSewer = useContentToken('feetransparency.utility_water_sewer');
  const utilityElectric = useContentToken('feetransparency.utility_electric');
  const utilityGas = useContentToken('feetransparency.utility_gas');

  const petFee = useContentToken('feetransparency.pet_fee');
  const petFeeSecondPet = useContentToken('feetransparency.pet_fee_second_pet');
  const petRent = useContentToken('feetransparency.pet_rent');

  const monthToMonthFee = useContentToken('feetransparency.month_to_month_fee');
  const transferFee = useContentToken('feetransparency.transfer_fee');
  const cleaningCharges = useContentToken('feetransparency.cleaning_charges');
  const damageCharges = useContentToken('feetransparency.damage_charges');
  const extraKeysOrLockChanges = useContentToken('feetransparency.extra_keys_or_lock_changes');
  const legalAttorneyFees = useContentToken('feetransparency.legal_attorney_fees');

  const showAll = isEditMode;

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    required: true,
  });
  const toggle = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const requiredRows: FeeRow[] = [
    {
      name: 'Application',
      value: applicationFee,
      frequency: 'One-Time',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={applicationFee} disclaimer="Per applicant">
          <EditableToken path="feetransparency.application_fee" as="span">{applicationFee}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Security Deposit (Refundable)',
      value: securityDeposit,
      frequency: 'One-Time at Move-In',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={securityDeposit} disclaimer="Starting at $750">
          <EditableToken path="feetransparency.security_deposit" as="span">{securityDeposit}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Reservation Fee',
      value: reservationFee,
      frequency: 'One-Time',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={reservationFee}>
          <EditableToken path="feetransparency.reservation_fee" as="span">{reservationFee}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Utility - New Account Fee',
      value: utilityNewAccountFee,
      frequency: 'One-Time',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={utilityNewAccountFee}>
          <EditableToken path="feetransparency.utility_new_account_fee" as="span">{utilityNewAccountFee}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Utility - Water/Sewer Service',
      value: utilityWaterSewer,
      frequency: 'Monthly',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={utilityWaterSewer}>
          <EditableToken path="feetransparency.utility_water_sewer" as="span">{utilityWaterSewer}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Utility - Electric Service',
      value: utilityElectric,
      frequency: 'Monthly',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={utilityElectric}>
          <EditableToken path="feetransparency.utility_electric" as="span">{utilityElectric}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Utility - Gas Service',
      value: utilityGas,
      frequency: 'Monthly',
      feeType: 'Required',
      badge: 'required',
      node: (
        <CostWithDisclaimer value={utilityGas}>
          <EditableToken path="feetransparency.utility_gas" as="span">{utilityGas}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
  ];

  const optionalRows: FeeRow[] = [
    {
      name: 'Pet Fee',
      value: petFee,
      frequency: 'One-Time',
      feeType: 'Optional',
      badge: 'optional',
      node: (
        <CostWithDisclaimer value={petFee}>
          <EditableToken path="feetransparency.pet_fee" as="span">{petFee}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Pet Fee - Second Pet',
      value: petFeeSecondPet,
      frequency: 'One-Time',
      feeType: 'Optional',
      badge: 'optional',
      node: (
        <CostWithDisclaimer value={petFeeSecondPet}>
          <EditableToken path="feetransparency.pet_fee_second_pet" as="span">{petFeeSecondPet}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Pet Rent',
      value: petRent,
      frequency: 'Monthly',
      feeType: 'Optional',
      badge: 'optional',
      node: (
        <CostWithDisclaimer value={petRent} disclaimer="Per pet">
          <EditableToken path="feetransparency.pet_rent" as="span">{petRent}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
  ];

  const situationalRows: FeeRow[] = [
    {
      name: 'Month To Month Fee',
      value: monthToMonthFee,
      frequency: 'Per Occurrence',
      feeType: 'Situational',
      badge: 'situational',
      node: (
        <CostWithDisclaimer value={monthToMonthFee}>
          <EditableToken path="feetransparency.month_to_month_fee" as="span">{monthToMonthFee}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Transfer Fee',
      value: transferFee,
      frequency: 'Per Occurrence',
      feeType: 'Situational',
      badge: 'situational',
      node: (
        <CostWithDisclaimer value={transferFee} disclaimer="Starting at $500">
          <EditableToken path="feetransparency.transfer_fee" as="span">{transferFee}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Cleaning Charges',
      value: cleaningCharges,
      frequency: 'Per Occurrence',
      feeType: 'Situational',
      badge: 'situational',
      node: (
        <CostWithDisclaimer value={cleaningCharges}>
          <EditableToken path="feetransparency.cleaning_charges" as="span">{cleaningCharges}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Damage Charges',
      value: damageCharges,
      frequency: 'Per Occurrence',
      feeType: 'Situational',
      badge: 'situational',
      node: (
        <CostWithDisclaimer value={damageCharges}>
          <EditableToken path="feetransparency.damage_charges" as="span">{damageCharges}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: 'Extra Keys Or Lock Changes',
      value: extraKeysOrLockChanges,
      frequency: 'Per Occurrence',
      feeType: 'Situational',
      badge: 'situational',
      node: (
        <CostWithDisclaimer value={extraKeysOrLockChanges}>
          <EditableToken path="feetransparency.extra_keys_or_lock_changes" as="span">{extraKeysOrLockChanges}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
    {
      name: "Legal/Attorney's Fees",
      value: legalAttorneyFees,
      frequency: 'Per Occurrence',
      feeType: 'Situational',
      badge: 'situational',
      node: (
        <CostWithDisclaimer value={legalAttorneyFees}>
          <EditableToken path="feetransparency.legal_attorney_fees" as="span">{legalAttorneyFees}</EditableToken>
        </CostWithDisclaimer>
      ),
    },
  ];

  const showRequired = showAll || requiredRows.some((r) => !isNA(r.value));
  const showOptional = showAll || optionalRows.some((r) => !isNA(r.value));
  const showSituational = showAll || situationalRows.some((r) => !isNA(r.value));
  const anyVisible = showRequired || showOptional || showSituational;

  return (
    <section className="ft-root">
      <div className={cn('ft-inner', wide && 'ft-inner--wide')}>
        {showHeader && (
          <>
            <EditableToken
              path="feetransparency.heading"
              as="h2"
              className="ft-heading"
            >
              {heading}
            </EditableToken>
            <RichTextContent
              path="feetransparency.intro"
              as="div"
              defaultValue="<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>"
              className="ft-blurb"
            />
          </>
        )}

        <div className="ft-accordion">
          {showRequired && (
            <FeePanel
              title="Figures to Help You Figure it Out"
              badgeLabel="Required"
              badgeVariant="required"
              description={
                <RichTextContent
                  path="feetransparency.required_intro"
                  as="div"
                  defaultValue="<p>Essential fees and utilities to help you understand your base costs and requirements.</p>"
                  className="ft-panel-desc"
                />
              }
              rows={requiredRows}
              open={isEditMode || !!openSections.required}
              onToggle={() => toggle('required')}
              showAll={showAll}
            />
          )}
          {showOptional && (
            <FeePanel
              title="Extras that Matter"
              badgeLabel="Optional"
              badgeVariant="optional"
              description={
                <RichTextContent
                  path="feetransparency.optional_intro"
                  as="div"
                  defaultValue="<p>Optional services, amenities, and rentable spaces to enhance your living experience. Typically charged per apartment but may be per person or pet. Contact the Leasing Team for further clarification.</p>"
                  className="ft-panel-desc"
                />
              }
              rows={optionalRows}
              open={isEditMode || !!openSections.optional}
              onToggle={() => toggle('optional')}
              showAll={showAll}
            />
          )}
          {showSituational && (
            <FeePanel
              title="Situational Fees"
              badgeLabel="Optional"
              badgeVariant="situational"
              description={
                <RichTextContent
                  path="feetransparency.situational_intro"
                  as="div"
                  defaultValue="<p>Sometimes life happens, and we understand things don&rsquo;t always go as planned. Situational fees may apply in certain circumstances and can include charges related to damage, excessive cleaning, or other incident-based services. These fees help us maintain a safe, clean, and comfortable community for all residents.</p>"
                  className="ft-panel-desc"
                />
              }
              rows={situationalRows}
              open={isEditMode || !!openSections.situational}
              onToggle={() => toggle('situational')}
              showAll={showAll}
            />
          )}
          {!anyVisible && (
            <p className="ft-none">
              No additional fees apply at this community. Please contact the
              leasing office for full details.
            </p>
          )}
        </div>

        <RichTextContent
          path="feetransparency.disclaimer"
          as="div"
          defaultValue="<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>"
          className="ft-disclaimer"
        />
      </div>

      <style>{`
        .ft-root {
          background: color-mix(in srgb, var(--vibe-color-text) 4%, var(--vibe-color-background));
          padding: 3.5rem 0 4.5rem;
        }
        @media (min-width: 768px) {
          .ft-root { padding: 4.5rem 0 5.5rem; }
        }

        .ft-inner {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        .ft-inner--wide {
          max-width: 90rem;
        }
        @media (min-width: 640px) {
          .ft-inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .ft-inner { padding: 0 2rem; }
          .ft-inner--wide { padding: 0 2.5rem; }
        }
        @media (min-width: 1400px) {
          .ft-inner--wide {
            max-width: 100rem;
            padding: 0 3rem;
          }
        }

        .ft-heading {
          margin: 0 0 0.75rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-weight: 400;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
          line-height: 1.2;
        }

        .ft-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin-bottom: 1.5rem;
          max-width: 48rem;
        }
        .ft-blurb p { margin: 0 0 0.75em; }
        .ft-blurb p:last-child { margin-bottom: 0; }
        .ft-blurb a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .ft-accordion {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .ft-panel {
          background: #ffffff;
          border-radius: 12px;
          overflow: visible;
        }

        .ft-panel-trigger {
          all: unset;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.5rem;
          cursor: pointer;
          background: #ffffff;
          text-align: left;
          transition: background-color 200ms ease;
        }
        .ft-panel-trigger:hover {
          background: color-mix(in srgb, var(--vibe-color-text) 2%, #ffffff);
        }
        .ft-panel-trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .ft-panel-title {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.75rem 0.95rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.15rem, 2vw, 1.5rem);
          font-weight: 400;
          line-height: 1.3;
          color: var(--vibe-color-text);
        }

        .ft-badge {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.03em;
          padding: 0.25rem 0.5rem;
          border-radius: 7px;
          vertical-align: middle;
          white-space: nowrap;
        }
        .ft-badge--required {
          background: #000000;
          color: #ffffff;
        }
        .ft-badge--optional,
        .ft-badge--situational {
          background: #e2e2e2;
          color: #000000;
        }

        .ft-panel-icon {
          position: relative;
          flex: 0 0 auto;
          width: 1.75rem;
          height: 1.75rem;
          border-radius: 9999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .ft-panel-icon::after {
          content: '+';
          font-family: var(--vibe-font-body);
          font-size: 1.75rem;
          font-weight: 300;
          line-height: 1;
          color: #4d5259;
          position: relative;
          top: -0.1em;
          transition: transform 250ms ease;
        }
        .ft-panel--open .ft-panel-icon::after {
          content: '−';
          top: 0;
        }

        .ft-panel-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 350ms ease;
          background: #ffffff;
        }
        .ft-panel-body[data-open='true'] {
          grid-template-rows: 1fr;
        }
        .ft-panel-body-inner {
          overflow: hidden;
          padding: 0 1.5rem;
        }
        .ft-panel-body[data-open='true'] .ft-panel-body-inner {
          overflow: visible;
        }
        .ft-panel-body[data-open='true'] .ft-panel-body-inner {
          padding-bottom: 1.5rem;
        }

        .ft-panel-desc {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.45;
          font-weight: 400;
          color: #686d74;
          margin-bottom: 1.5rem;
        }
        .ft-panel-desc p { margin: 0 0 0.5em; }
        .ft-panel-desc p:last-child { margin-bottom: 0; }

        .ft-table-wrap {
          width: 100%;
          overflow: visible;
        }
        @media (max-width: 767px) {
          .ft-table-wrap {
            overflow-x: visible;
          }
        }
        .ft-table {
          width: 100%;
          min-width: 36rem;
          border-collapse: collapse;
          table-layout: fixed;
          margin: 0;
        }
        .ft-table th {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.4;
          color: #000000;
          text-align: left;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e0e0e0;
        }
        .ft-table th:nth-child(1),
        .ft-table td:nth-child(1) {
          width: 40%;
          padding-left: 0;
        }
        .ft-table th:nth-child(2),
        .ft-table td:nth-child(2) {
          width: 22%;
          text-align: left;
        }
        .ft-table th:nth-child(3),
        .ft-table td:nth-child(3) {
          width: 22%;
          text-align: left;
          white-space: nowrap;
        }
        .ft-table th:nth-child(4),
        .ft-table td:nth-child(4) {
          width: 16%;
          text-align: right;
          padding-right: 0;
          white-space: nowrap;
        }
        .ft-table td {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          font-weight: 400;
          color: #4d5259;
          padding: 0.95rem 1rem;
          border-bottom: 1px solid #e0e0e0;
          vertical-align: middle;
          overflow-wrap: anywhere;
        }
        .ft-table tr:last-child td { border-bottom: none; }

        .ft-cost-wrap {
          position: relative;
          display: inline-block;
          max-width: 100%;
        }
        .ft-cost {
          font-variant-numeric: tabular-nums;
        }
        .ft-cost--tooltip {
          border-bottom: 2px dotted currentColor;
          cursor: help;
        }
        .ft-cost--dollar::before {
          content: '$';
        }
        .ft-cost-tip {
          position: absolute;
          left: 0;
          top: calc(100% + 0.45rem);
          z-index: 20;
          width: max-content;
          max-width: min(16rem, 70vw);
          padding: 0.45rem 0.65rem;
          border-radius: 8px;
          background: #1f2933;
          color: #ffffff;
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 400;
          line-height: 1.35;
          text-align: left;
          box-shadow: 0 8px 20px color-mix(in srgb, #000 18%, transparent);
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transform: translateY(-0.15rem);
          transition: opacity 150ms ease, transform 150ms ease, visibility 150ms ease;
        }
        .ft-cost-tip::after {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 0.85rem;
          border: 5px solid transparent;
          border-bottom-color: #1f2933;
        }
        .ft-cost-wrap--tip:hover .ft-cost-tip,
        .ft-cost-wrap--tip:focus-within .ft-cost-tip {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .ft-empty {
          margin: 0;
          padding: 0.5rem 0 0.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .ft-none {
          margin: 0;
          padding: 1.5rem;
          border-radius: 12px;
          background: #ffffff;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .ft-disclaimer {
          margin-top: 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        .ft-disclaimer p { margin: 0 0 0.5em; }
        .ft-disclaimer p:last-child { margin-bottom: 0; }
        .ft-disclaimer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        @media (max-width: 1200px) {
          .ft-panel-title {
            flex-direction: column;
            align-items: flex-start;
          }
          .ft-table {
            min-width: 0;
          }
          .ft-table th,
          .ft-table td {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
          .ft-table th:nth-child(1),
          .ft-table td:nth-child(1) { padding-left: 0; }
          .ft-table th:nth-child(4),
          .ft-table td:nth-child(4) { padding-right: 0; }
        }

        @media (max-width: 767px) {
          .ft-panel-trigger {
            padding: 1.125rem;
            align-items: flex-start;
          }
          .ft-panel-title { font-size: 1.25rem; width: 80%; }
          .ft-panel-body-inner { padding-left: 1.125rem; padding-right: 1.125rem; }
          .ft-panel-body[data-open='true'] .ft-panel-body-inner {
            padding-bottom: 1.125rem;
          }
          .ft-table-wrap {
            overflow-x: visible;
          }
          .ft-table {
            min-width: 0;
          }
          .ft-table thead { display: none; }
          .ft-table,
          .ft-table tbody,
          .ft-table tr,
          .ft-table td {
            display: block;
            width: 100%;
          }
          .ft-table tr {
            padding: 0.85rem 0;
            border-bottom: 1px solid #e0e0e0;
          }
          .ft-table tr:last-child {
            border-bottom: none;
          }
          .ft-table td {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            width: auto;
            padding: 0.3rem 0;
            border-bottom: none;
            text-align: right;
            white-space: normal;
            font-size: 0.9375rem;
          }
          .ft-table td::before {
            content: attr(data-label);
            flex: 0 1 auto;
            font-weight: 600;
            color: #2c3e50;
            text-align: left;
          }
          .ft-table td:first-child {
            justify-content: flex-start;
            text-align: left;
            font-weight: 500;
            color: var(--vibe-color-text);
            padding-bottom: 0.45rem;
          }
          .ft-table td:first-child::before {
            display: none;
          }
          .ft-table th:nth-child(2),
          .ft-table td:nth-child(2),
          .ft-table th:nth-child(3),
          .ft-table td:nth-child(3),
          .ft-table th:nth-child(4),
          .ft-table td:nth-child(4) {
            width: auto;
            text-align: right;
            white-space: normal;
          }
          .ft-disclaimer { text-align: center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .ft-panel-body,
          .ft-panel-icon::after,
          .ft-cost-tip {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeTransparency',
  displayName: 'Fee Transparency Table',
  tokens: {
    'feetransparency.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Fee Transparency',
    },
    'feetransparency.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>',
    },
    'feetransparency.required_intro': {
      label: 'Required Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Essential fees and utilities to help you understand your base costs and requirements.</p>',
    },
    'feetransparency.optional_intro': {
      label: 'Optional Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Optional services, amenities, and rentable spaces to enhance your living experience. Typically charged per apartment but may be per person or pet. Contact the Leasing Team for further clarification.</p>',
    },
    'feetransparency.situational_intro': {
      label: 'Situational Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Sometimes life happens, and we understand things don&rsquo;t always go as planned. Situational fees may apply in certain circumstances and can include charges related to damage, excessive cleaning, or other incident-based services. These fees help us maintain a safe, clean, and comfortable community for all residents.</p>',
    },

    'feetransparency.application_fee': { label: 'Application', type: 'text', defaultField: 'property_application_fee', defaultValue: '$50' },
    'feetransparency.security_deposit': { label: 'Security Deposit (Refundable)', type: 'text', defaultValue: '$750' },
    'feetransparency.reservation_fee': { label: 'Reservation Fee', type: 'text', defaultValue: '$200' },
    'feetransparency.utility_new_account_fee': { label: 'Utility - New Account Fee', type: 'text', defaultValue: '$10' },
    'feetransparency.utility_water_sewer': { label: 'Utility - Water/Sewer Service', type: 'text', defaultValue: 'Usage-Based (Utilities)' },
    'feetransparency.utility_electric': { label: 'Utility - Electric Service', type: 'text', defaultValue: 'Usage-Based (Utilities)' },
    'feetransparency.utility_gas': { label: 'Utility - Gas Service', type: 'text', defaultValue: 'Usage-Based (Utilities)' },

    'feetransparency.pet_fee': { label: 'Pet Fee', type: 'text', defaultField: 'property_upfront_pet_fee', defaultValue: '$350' },
    'feetransparency.pet_fee_second_pet': { label: 'Pet Fee - Second Pet', type: 'text', defaultValue: '$150' },
    'feetransparency.pet_rent': { label: 'Pet Rent', type: 'text', defaultField: 'property_monthly_pet_rent', defaultValue: '$55' },

    'feetransparency.month_to_month_fee': { label: 'Month To Month Fee', type: 'text', defaultValue: '$350' },
    'feetransparency.transfer_fee': { label: 'Transfer Fee', type: 'text', defaultValue: '$500' },
    'feetransparency.cleaning_charges': { label: 'Cleaning Charges', type: 'text', defaultValue: 'Varies' },
    'feetransparency.damage_charges': { label: 'Damage Charges', type: 'text', defaultValue: 'Varies' },
    'feetransparency.extra_keys_or_lock_changes': { label: 'Extra Keys Or Lock Changes', type: 'text', defaultValue: 'Varies' },
    'feetransparency.legal_attorney_fees': { label: "Legal/Attorney's Fees", type: 'text', defaultValue: 'Varies' },

    'feetransparency.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue: '<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>',
    },
  },
};

(FeeTransparency as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeTransparency;
